import ko from 'knockout';
import numeral from 'numeral';
import { ClockProcessStatus, RequestRegistrationStatus } from './databaseModels';

export const formatMargin = (hours: number, margin: number) => {
    const formattedMargin = numeral(margin).format('$0,0.00');
    return `${formattedMargin} (${Math.round(hours)}u)`;
}

export const formatMarginNoHours = (margin: number) => {
    const formattedMargin = numeral(margin).format('$0,0.00');
    return formattedMargin;
}

export class RequestOverviewMarginRegistrationModel {
    timeWorked: KnockoutObservable<number>;
    status: KnockoutObservable<RequestRegistrationStatus>;
    processStatus: KnockoutObservable<ClockProcessStatus>;
    ageOnRequestDate: KnockoutObservable<number>;

    constructor(model: any = {}) {
        this.timeWorked = ko.observable<number>(model.timeWorked);
        this.status = ko.observable<RequestRegistrationStatus>(model.status);
        this.processStatus = ko.observable<ClockProcessStatus>(model.processStatus);
        this.ageOnRequestDate = ko.observable<number>(model.ageOnRequestDate);
    }
}

export class RequestOverviewMarginModel {
    id: KnockoutObservable<string>;
    amount: KnockoutObservable<number>;
    commissionUnder20: KnockoutObservable<number>;
    commission20OrAbove: KnockoutObservable<number>;
    hourlyRate: KnockoutObservable<number>;
    registrations: KnockoutObservableArray<RequestOverviewMarginRegistrationModel>;

    pendingAmount: KnockoutComputed<number>;
    acceptedAmount: KnockoutComputed<number>;
    maxPendingAmount: KnockoutComputed<number>;
    acceptedRegistrations: KnockoutComputed<RequestOverviewMarginRegistrationModel[]>;
    pendingRegistrations: KnockoutComputed<RequestOverviewMarginRegistrationModel[]>;
    remainingAmount: KnockoutComputed<number>;
    hoursAvailableMin: KnockoutObservable<number>;
    targetHours: KnockoutComputed<number>;
    pendingHours: KnockoutComputed<number>;
    plannedHours: KnockoutComputed<number>;
    remainingHours: KnockoutComputed<number>;
    commissionFactor: KnockoutComputed<number>;
    targetMargin: KnockoutComputed<number>;
    targetMarginMax: KnockoutComputed<number>;
    pendingMargin: KnockoutComputed<number>;
    plannedMargin: KnockoutComputed<number>;
    remainingMargin: KnockoutComputed<number>;

    lowestCommission: KnockoutComputed<number>;
    highestCommission: KnockoutComputed<number>;

    constructor(model: any = {}) {
        this.id = ko.observable<string>(model.id);
        this.amount = ko.observable<number>(model.amount);
        this.commissionUnder20 = ko.observable<number>(model.commissionUnder20);
        this.commission20OrAbove = ko.observable<number>(model.commission20OrAbove);
        this.hourlyRate = ko.observable<number>(model.hourlyRate);
        this.registrations = ko.observableArray<RequestOverviewMarginRegistrationModel>(model.registrations?.map((rr: any) => new RequestOverviewMarginRegistrationModel(rr)));

        this.lowestCommission = ko.pureComputed(() => Math.min(this.commission20OrAbove(), this.commissionUnder20()));
        this.highestCommission = ko.pureComputed(() => Math.max(this.commission20OrAbove(), this.commissionUnder20()));
        this.hoursAvailableMin = ko.observable(model.hoursAvailableMin);

        this.acceptedRegistrations = ko.pureComputed(() => this.registrations().filter(r => r.status() === RequestRegistrationStatus.Accepted));
        this.pendingRegistrations = ko.pureComputed(() => this.registrations().filter(r => r.status() === RequestRegistrationStatus.Pending));

        this.pendingAmount = ko.pureComputed(() => this.pendingRegistrations().length);
        this.acceptedAmount = ko.pureComputed(() => this.acceptedRegistrations().length);
        this.maxPendingAmount = ko.pureComputed(() => Math.min(this.pendingAmount(), this.amount() - this.acceptedAmount()));
        this.remainingAmount = ko.pureComputed(() => this.amount() - this.acceptedAmount());

        this.targetHours = ko.pureComputed(() => this.amount() * this.hoursAvailableMin())
        this.pendingHours = ko.pureComputed(() => this.maxPendingAmount() * this.hoursAvailableMin());
        this.plannedHours = ko.pureComputed(() => this.acceptedAmount() * this.hoursAvailableMin());
        this.remainingHours = ko.pureComputed(() => this.remainingAmount() * this.hoursAvailableMin());

        this.targetMargin = ko.pureComputed(() => this.targetHours() * this.hourlyRate() * this.calculateCommissionFactor(this.lowestCommission()));
        this.targetMarginMax = ko.pureComputed(() => this.targetHours() * this.hourlyRate() * this.calculateCommissionFactor(this.highestCommission()));
        this.pendingMargin = ko.pureComputed(() => this.pendingRegistrations()
            .sort((regA, regB) => regA.ageOnRequestDate() - regB.ageOnRequestDate())
            .slice(0, this.maxPendingAmount())
            .reduce((total: number, registration: RequestOverviewMarginRegistrationModel) => {
                const commissionPercentage = registration.ageOnRequestDate() < 20
                    ? this.commissionUnder20()
                    : this.commission20OrAbove();

                const commissionFactor = this.calculateCommissionFactor(commissionPercentage);

                return total + (this.hoursAvailableMin() * this.hourlyRate() * commissionFactor);
           }, 0));

        this.plannedMargin = ko.pureComputed(() => this.acceptedRegistrations().reduce((total: number, registration: RequestOverviewMarginRegistrationModel) => {
            const commissionPercentage = registration.ageOnRequestDate() < 20
                ? this.commissionUnder20()
                : this.commission20OrAbove();

            const commissionFactor = this.calculateCommissionFactor(commissionPercentage);

            return total + (this.hoursAvailableMin() * this.hourlyRate() * commissionFactor);
        }, 0));
        this.remainingMargin = ko.pureComputed(() => this.remainingHours() * this.hourlyRate() * this.calculateCommissionFactor(this.lowestCommission()));
    }

    private calculateCommissionFactor(commissionPercentage: number) {
        return commissionPercentage / 100;
    }
}
